import { Hex, SelectListItemRenderer, Stroke } from '@acies/core';
import { ComponentCO2 } from '@acies/domain';
import { CheckBox, Label, theme } from '@acies/ui';

export default class ComponentCO2ItemRenderer extends SelectListItemRenderer<ComponentCO2> {
	#checkBox!: CheckBox<ComponentCO2>;
	#epdLabel!: Label;
	#nameLabel!: Label;
	#descriptionLabel!: Label;
	#updatedByLabel!: Label;
	#updatedAtLabel!: Label;
	#focusStroke: Stroke;
	public constructor() {
		super();
		this.width = 'fill';
		this.height = 32;
		this.alignment = 'left';
		this.paddingHorizontal = 16;
		this.gapHorizontal = 16;
		this.cornerRadius = 6;
		this.cursor = 'pointer';
		// TODO:Martin temporary stroke indicator that will be implemented correctly in another task.
		this.#focusStroke = new Stroke(new Hex('#47C2FF'), 2, 'inside');
		this.selectedObservableValueChanged = this.selectedObservableValueChanged.bind(this);
	}

	override dispose(): void {
		super.dispose();
		if (this.item && this.item.selected) {
			this.item.selected.unsubscribe(this.selectedObservableValueChanged);
		}
	}

	/** Initialize() is called once, the first time the component is added as a Node child, so we create our children here, since we can't do it in the constructor because Angular runs the constructor without access to the DOM api's, so adding node children will fail. */
	protected override initialize(): void {
		super.initialize();
		this.#createChildren();
	}

	/** When the SelectList creates the ItemRenderer children and add them as children, the SelectList sets the item property to the item in the data Array. We can then override the itemChanged() to update the ITemRenderer components display data. */
	protected override itemChanged(): void {
		super.itemChanged();
		this.checkBox.item = this.item;
		if (this.item) {
			this.epdLabel.text = this.item.epd;
			this.nameLabel.text = this.item.name;
			this.descriptionLabel.text = this.item.description;
			this.updatedByLabel.text = this.item.updatedByFormatted;
			this.updatedAtLabel.text = this.item.updatedAtFormatted;
			this.item.selected.subscribe(this.selectedObservableValueChanged);
		} else {
			// remember to reset the checkBox state when the item changes,
			// since we are pooling ItemRenderers in the SelectList for performance reasons.
			this.checkBox.selected = false;
		}
	}

	protected override selectedChanged(): void {
		super.selectedChanged();
		this.#updateSelectedFocusedStyles();
	}

	/** The ComponentCO2ItemRenderer extends InteractiveContainer that has state so we can react to these changes, by overriding the stateChanged() method and in this case, show / hide focus indication. */
	protected override stateChanged(): void {
		super.stateChanged();
		this.#updateSelectedFocusedStyles();
	}

	get #isFocused(): boolean {
		return this.state.toLowerCase().includes('focused');
	}

	#updateSelectedFocusedStyles(): void {
		if (this.#isFocused) {
			this.stroke = this.#focusStroke;
			this.fill = theme.colors.fill.focus;
		} else {
			this.stroke = null;
			if (this.selected) {
				this.fill = theme.colors.fill.soft;
			} else {
				this.fill = null;
			}
		}
	}

	private get epdLabel(): Label {
		if (!this.#epdLabel) {
			this.#epdLabel = new Label();
			this.#epdLabel.width = 128;
		}
		return this.#epdLabel;
	}

	private get nameLabel(): Label {
		if (!this.#nameLabel) {
			this.#nameLabel = new Label();
			this.#nameLabel.width = 128;
		}
		return this.#nameLabel;
	}

	private get descriptionLabel(): Label {
		if (!this.#descriptionLabel) {
			this.#descriptionLabel = new Label();
			this.#descriptionLabel.width = 'fill';
		}
		return this.#descriptionLabel;
	}

	private get updatedByLabel(): Label {
		if (!this.#updatedByLabel) {
			this.#updatedByLabel = new Label();
			this.#updatedByLabel.width = 128;
		}
		return this.#updatedByLabel;
	}

	private get updatedAtLabel(): Label {
		if (!this.#updatedAtLabel) {
			this.#updatedAtLabel = new Label();
			this.#updatedAtLabel.width = 96;
			this.#updatedAtLabel.textAlignHorizontal = 'right';
		}
		return this.#updatedAtLabel;
	}

	private get checkBox(): CheckBox<ComponentCO2> {
		if (!this.#checkBox) {
			this.#checkBox = new CheckBox();
		}
		return this.#checkBox;
	}

	private selectedObservableValueChanged(value: boolean): void {
		this.checkBox.selected = value;
	}

	#createChildren(): void {
		this.addComponents([this.checkBox, this.nameLabel, this.epdLabel, this.descriptionLabel, this.updatedByLabel, this.updatedAtLabel]);
	}
}
customElements.define('ac-component-co2-item-renderer', ComponentCO2ItemRenderer);
